import { computed } from 'vue'

import type { ComputedRef, WritableComputedRef } from 'vue'
import type { AppSelectOption } from '@brighterly/lib-core-types/ui/selects/interfaces'
import type { AppFormState } from '@shared/ui/fields'
import type { TasksKanbanStage } from '@brighterly/lib-core-types/tasks-kanban/enums'
import type { TaskPriority } from '@entities/task'
import type { TaskType } from '@shared/config'

import { useListsStore } from '@shared/model'
import { TasksKanbanRelatedToType } from '@shared/config'

import { useTasksKanbanStore } from './store'

interface TaskGeneralInfoCreateReturn {
  refTaskType: WritableComputedRef<string | undefined>
  refTaskTypeOptions: ComputedRef<AppSelectOption<string>[]>
  refTaskTypeState: ComputedRef<AppFormState>
  refStage: WritableComputedRef<TasksKanbanStage | undefined>
  refStageOptions: ComputedRef<AppSelectOption<TasksKanbanStage>[]>
  refStageState: ComputedRef<AppFormState>
  refPriority: WritableComputedRef<TaskPriority | undefined>
  refPriorityOptions: ComputedRef<AppSelectOption<TaskPriority>[]>
}

export const useTaskGeneralInfoCreate = (): TaskGeneralInfoCreateReturn => {
  const tasksKanbanStore = useTasksKanbanStore()
  const listsStore = useListsStore()
  const route = useRoute()

  const refRelatedToType = computed<TasksKanbanRelatedToType | null>(() => {
    const value = (route?.query?.related_to_type || null) as TasksKanbanRelatedToType | null

    const allowed = Object.values(TasksKanbanRelatedToType) as string[]
    return typeof value === 'string' && allowed.includes(value)
      ? value as TasksKanbanRelatedToType
      : null
  })

  const refTaskTypeOptions = computed<AppSelectOption<string>[]>(() => {
    if (!refRelatedToType.value) return []

    const typeLabels = listsStore.getTasksKanbanList?.task_type ?? {}
    const teamLabels = listsStore.getTasksKanbanList?.team ?? {}
    const taskTypes = listsStore.getTaskTypes ?? {}

    return Object.entries(typeLabels)
      .filter(([value]) => { // FIXME: ts-expect-error
        // @ts-expect-error any
        return taskTypes[value]?.related_to === refRelatedToType.value
      })
      .map(([value, typeLabel]) => { // FIXME: ts-expect-error
        // @ts-expect-error any
        const team = taskTypes[value]?.team ?? null
        const teamLabel = team ? teamLabels[team] ?? team : ''
        const text = teamLabel ? `${typeLabel} (${teamLabel})` : typeLabel

        return { value, text, teamLabel }
      })
      .sort((a, b) => a.teamLabel.localeCompare(b.teamLabel) || a.text.localeCompare(b.text))
      .map(({ value, text }) => ({ value, text }))
  })

  const refTaskType = computed<TaskType | undefined>({
    get: () => tasksKanbanStore.getTaskType ?? undefined,
    set: (value) => {
      const taskType = value ?? null
      const team = taskType
        ? listsStore.getLists?.tasks_kanban?.config?.task_types?.[taskType]?.team ?? null
        : null

      tasksKanbanStore.setTaskType(taskType)
      tasksKanbanStore.setTeam(team)
    },
  })

  const refStageOptions = computed<AppSelectOption<TasksKanbanStage>[]>(() => {
    const labels = listsStore.getLists?.tasks_kanban?.lists?.status ?? {}
    return Object.entries(labels).map(([value, text]) => ({
      value: value as TasksKanbanStage,
      text,
    }))
  })

  const refStage = computed<TasksKanbanStage | undefined>({
    get: () => tasksKanbanStore.getStage ?? undefined,
    set: (value) => {
      tasksKanbanStore.setStage(value ?? null)
    },
  })

  const refPriorityOptions = computed<AppSelectOption<TaskPriority>[]>(() => {
    const labels = listsStore.getTasksKanbanList?.priority ?? {}
    return Object.entries(labels).map(([value, text]) => ({ value: value as TaskPriority, text }))
  })

  const refPriority = computed<TaskPriority | undefined>({
    get: () => tasksKanbanStore.getPriority ?? undefined,
    set: value => tasksKanbanStore.setPriority(value ?? null),
  })

  const refTaskTypeState = computed<AppFormState>(() =>
    tasksKanbanStore.getSubmitted && !tasksKanbanStore.getTaskType ? 'error' : 'static',
  )

  const refStageState = computed<AppFormState>(() =>
    tasksKanbanStore.getSubmitted && !tasksKanbanStore.getStage ? 'error' : 'static',
  )

  return {
    refTaskType,
    refTaskTypeOptions,
    refTaskTypeState,
    refStage,
    refStageOptions,
    refStageState,
    refPriority,
    refPriorityOptions,
  }
}
